import {
  extractVariables,
  normalizeFieldMapping,
  getRequestDerivedValues,
  resolveValue,
  renderTemplate
} from '../utils/testVendorRequest';
import { useMemo, useState, useEffect, useCallback } from 'react';
import type {
  FieldMapping,
  Handler,
  HandlerLookupResult,
  NormalizedFieldMapping,
  RequestInitialValues,
  TestProfile,
  VariableValues,
} from '../types/testVendorRequest.types';

// ============ Custom Hooks ============

interface FormValues {
  [key: string]: string | number | Handler | null | undefined;
}

interface RequestLocalState {
  localUrl: string;
  localBody: string;
  localMethod: string;
  localHandler: Handler | number | string | null;
  localTimeout: string;
  localHeaders: [string, string, string];
}

interface UseRequestFieldsReturn {
  normalizedFields: NormalizedFieldMapping;
  localUrl: string;
  setLocalUrl: (value: string) => void;
  localBody: string;
  setLocalBody: (value: string) => void;
  localMethod: string;
  setLocalMethod: (value: string) => void;
  localHeaders: [string, string, string];
  setLocalHeaders: (updater: [string, string, string] | ((prev: [string, string, string]) => [string, string, string])) => void;
  localHandler: Handler | number | string | null;
  setLocalHandler: (value: Handler | number | string | null) => void;
  localTimeout: string;
  setLocalTimeout: (value: string) => void;
  updateHeader: (index: number, value: string) => void;
}

/**
 * Custom hook for managing request fields.
 */
export const useRequestFields = (
  formValues: FormValues,
  fieldMapping: FieldMapping | undefined,
  prefix: string | undefined,
  isOpen: boolean,
  initialValues?: RequestInitialValues
): UseRequestFieldsReturn => {
  const normalizedFields = useMemo<NormalizedFieldMapping>(
    () => normalizeFieldMapping(fieldMapping, prefix),
    [fieldMapping, prefix],
  );

  const buildLocalState = (
    values: FormValues,
    fields: NormalizedFieldMapping,
    providedInitialValues?: RequestInitialValues
  ): RequestLocalState => {
    const hasInitialValues = providedInitialValues &&
      typeof providedInitialValues === 'object' &&
      (providedInitialValues.url || providedInitialValues.body ||
        providedInitialValues.method || providedInitialValues.handler != null);

    if (hasInitialValues) {
      const headers = Array.isArray(providedInitialValues.headers)
        ? providedInitialValues.headers
        : ['', '', ''];
      return {
        localUrl: providedInitialValues.url || '',
        localBody: providedInitialValues.body || '',
        localMethod: providedInitialValues.method || '',
        localHandler: providedInitialValues.handler ?? null,
        localTimeout: String(providedInitialValues.timeout ?? ''),
        localHeaders: [headers[0] || '', headers[1] || '', headers[2] || ''],
      };
    }
    return {
      localUrl: String(values?.[fields.urlKey] ?? ''),
      localBody: String(values?.[fields.bodyKey] ?? ''),
      localMethod: String(values?.[fields.methodKey] ?? ''),
      localHandler: (values?.[fields.handlerIdKey] as Handler | number | string | null) ?? null,
      localTimeout: '',
      localHeaders: [
        String(values?.[fields.headerKeys[0]] ?? ''),
        String(values?.[fields.headerKeys[1]] ?? ''),
        String(values?.[fields.headerKeys[2]] ?? ''),
      ],
    };
  };

  const [localState, setLocalState] = useState<RequestLocalState>(() => buildLocalState(formValues, normalizedFields, initialValues));

  useEffect(() => {
    setLocalState(buildLocalState(formValues, normalizedFields, initialValues));
  }, [isOpen, normalizedFields, initialValues]);

  const setLocalUrl = useCallback((value: string): void => {
    setLocalState((prev) => ({ ...prev, localUrl: value }));
  }, []);

  const setLocalBody = useCallback((value: string): void => {
    setLocalState((prev) => ({ ...prev, localBody: value }));
  }, []);

  const setLocalMethod = useCallback((value: string): void => {
    setLocalState((prev) => ({ ...prev, localMethod: value }));
  }, []);

  const setLocalHandler = useCallback((value: Handler | number | string | null): void => {
    setLocalState((prev) => ({ ...prev, localHandler: value }));
  }, []);

  const setLocalTimeout = useCallback((value: string): void => {
    setLocalState((prev) => ({ ...prev, localTimeout: value }));
  }, []);

  const setLocalHeaders = useCallback((updater: [string, string, string] | ((prev: [string, string, string]) => [string, string, string])): void => {
    setLocalState((prev) => {
      const nextHeaders = typeof updater === 'function' ? updater(prev.localHeaders) : updater;
      return { ...prev, localHeaders: nextHeaders };
    });
  }, []);

  const updateHeader = useCallback((index: number, value: string): void => {
    setLocalHeaders((prev) => {
      const next: [string, string, string] = [...prev];
      next[index] = value;
      return next;
    });
  }, [setLocalHeaders]);

  return {
    normalizedFields,
    localUrl: localState.localUrl,
    setLocalUrl,
    localBody: localState.localBody,
    setLocalBody,
    localMethod: localState.localMethod,
    setLocalMethod,
    localHeaders: localState.localHeaders,
    setLocalHeaders,
    localHandler: localState.localHandler,
    setLocalHandler,
    localTimeout: localState.localTimeout,
    setLocalTimeout,
    updateHeader,
  };
};

/**
 * Custom hook for deriving values from a request.
 */
export const useRequestDerivedValues = (
  localUrl: string,
  localBody: string,
  localMethod: string,
  selectedProfile: TestProfile | null
): Record<string, string | null> => {
  return useMemo(() => {
    const merged: VariableValues = { ...(selectedProfile || {}) };
    const renderedUrl = renderTemplate(localUrl || '', merged);
    const renderedBody = localMethod && localMethod !== 'GET' ? renderTemplate(localBody || '', merged) : '';
    return getRequestDerivedValues(renderedUrl, renderedBody);
  }, [localUrl, localBody, localMethod, selectedProfile]);
};

interface UseVariableDetectionReturn {
  variables: string[];
  profileKeys: string[];
}

/**
 * Custom hook for detecting variables in a request.
 */
export const useVariableDetection = (
  localUrl: string,
  localBody: string,
  localMethod: string,
  selectedProfile: TestProfile | null
): UseVariableDetectionReturn => {
  const variables = useMemo<string[]>(() => {
    const fromUrl = extractVariables(localUrl);
    const method = (localMethod || '').toUpperCase();
    const fromBody = method && method !== 'GET' ? extractVariables(localBody) : [];
    const all = [...fromUrl, ...fromBody];
    return Array.from(new Set(all));
  }, [localUrl, localBody, localMethod]);

  const profileKeys = useMemo<string[]>(() => {
    if (!selectedProfile) return [];
    return Object.keys(selectedProfile).filter((key) => key !== 'id' && key !== 'name');
  }, [selectedProfile]);

  return { variables, profileKeys };
};

/**
 * Seed variables from the request.
 */
export const useSeedVariables = (
  visibleNames: string[],
  profileKeys: string[],
  requestDerivedValues: Record<string, string | null>
): (profile: TestProfile | null) => Record<string, string> => {
  return useCallback((profile: TestProfile | null): Record<string, string> => {
    if (!profile) return {};
    const namesToSeed = Array.isArray(visibleNames) ? visibleNames : [];
    if (!namesToSeed.length) return {};

    const seed: Record<string, string> = {};
    namesToSeed.forEach((key) => {
      if (key in requestDerivedValues) {
        const value = requestDerivedValues[key];
        seed[key] = value === null ? '' : String(value);
        return;
      }
      const fromProfile = resolveValue(profile as unknown as VariableValues, key);
      if (fromProfile !== undefined && fromProfile !== null && String(fromProfile) !== '') {
        seed[key] = String(fromProfile);
        return;
      }
    });

    return seed;
  }, [visibleNames, requestDerivedValues]);
};

/**
 * Display a handler in a user-friendly way.
 */
export const useHandlerDisplay = (httpSuccessHandler: Handler | number | string | null): string => {
  return useMemo(() => {
    if (!httpSuccessHandler) return 'not selected';
    if (typeof httpSuccessHandler === 'object') {
      const id = httpSuccessHandler.id ?? httpSuccessHandler.value;
      const name = httpSuccessHandler.name ?? httpSuccessHandler.label;
      return id != null ? `ID ${id}` : name || 'selected';
    }
    return `ID ${httpSuccessHandler}`;
  }, [httpSuccessHandler]);
};

/**
 * Lookup a handler by ID.
 */
export const useHandlerLookup = (
  allHandlersFromState: Handler[],
  handlerId: Handler | number | string | null
): HandlerLookupResult => {
  return useMemo(() => {
    const handlers = Array.isArray(allHandlersFromState) ? allHandlersFromState : [];
    let actualId: number | string | null | undefined = handlerId as number | string | null;
    if (handlerId && typeof handlerId === 'object') {
      actualId = handlerId.id ?? handlerId.value;
    }
    if (actualId == null) return { code: '', name: '' };

    const found = handlers.find((handler) => String(handler.id) === String(actualId));
    return {
      code: found?.code || found?.handler || '',
      name: found?.name || '',
    };
  }, [allHandlersFromState, handlerId]);
};

/**
 * Parse a comma-separated list of status codes into an array of numbers.
 */
export const useParseStatusCodes = (statusString: string | undefined): number[] => {
  return useMemo(() => {
    return (statusString || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => parseInt(s, 10))
      .filter((n) => !Number.isNaN(n));
  }, [statusString]);
};

